import { tv } from "@utils/tw"
import { useRouterEvents } from "eddev/routing"
import { useEffect, useState } from "react"

export function PageLoadIndicator() {
  const [state, setState] = useState<"loading" | "nearly" | "ready">()

  // `useRouterState`'s `pendingRoute` is set once a navigation starts but is
  // never cleared back to `undefined` by eddev's router after it finishes
  // (it's carried forward by the router's shallow state merge), so deriving
  // `isLoading` from it only ever fires once per page load and then gets
  // stuck. `navigate:start`/`navigate:changed` are the reliable, symmetric
  // pair (also used by `<ScrollRestoration />`) for "a navigation began" /
  // "a navigation finished".
  useRouterEvents((event) => {
    if (event.type === "navigate:start") {
      setState("loading")
    } else if (event.type === "navigate:changed") {
      setState("ready")
    }
  })

  useEffect(() => {
    if (state === "ready") {
      const timer = setTimeout(() => {
        setState(undefined)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [state === "ready"])

  return <div className={indicatorStyles({ state })} />
}

// `$$height` (stitches local CSS custom property, "2px") -> inlined as a
// literal everywhere it was used (`height` and the `ready` state's
// `translateY(-$$height)`), since it was only ever set once and never
// varied at a call site.
//
// Every state uses the arbitrary-property `[transition:...]` form (rather
// than the named `transition-[...]` + `duration-*` utilities used
// elsewhere) so all four alternatives — the base "off" state and the three
// `state` variants — land in the same tailwind-merge class group (keyed by
// the literal property name "transition"). That guarantees only one of
// them survives `cn()`'s de-duplication, regardless of Tailwind's internal
// utility ordering — required here because `ready`'s two transitioned
// properties (`width`/`transform`) need different durations *and* a delay
// on only one of them, which the named duration/delay utilities can't
// express per-property.
const indicatorStyles = tv({
  base: "fixed top-0 left-0 h-[2px] bg-highlight z-[10000] w-0 [transition:none]",
  variants: {
    state: {
      loading: "w-[70%] [transition:width_2s]",
      nearly: "w-[80%] [transition:width_2s]",
      ready: "w-full -translate-y-[2px] [transition:width_0.3s,transform_0.2s_0.3s]",
    },
  },
})
